import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calculator, Calendar, TrendingUp, Home, FileText, Truck, PieChart, Umbrella, CreditCard, Percent, PiggyBank, LineChart, ArrowRight, DollarSign, Wrench, Search } from 'lucide-react';

const MotionCard = motion(Card);

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 }
};

const SearchBox = () => (
  <div className="relative w-full max-w-2xl mb-12">
    <input
      type="text"
      placeholder="Search for a calculator..."
      className="w-full px-4 py-3 text-[#29261b] bg-white border rounded-full focus:outline-none focus:border-red-500 shadow-md"
    />
    <button className="absolute right-0 top-0 mt-3 mr-4">
      <Search className="h-6 w-6 text-red-500" />
    </button>
  </div>
);

const Dashboard = () => {
  const cards = [
    {
      title: 'Interest Calculators',
      description: 'Simple Interest. Simple Discount. Compound Interest. Effective Interest Rates. Principal, Interest Rates and Time.',
      icon: <Percent className="w-8 h-8 text-orange-500" />,
      link: '/interest-calculators', // Updated link
      buttonText: 'View Calculators',
      color: 'from-orange-400 to-orange-600'
    },
    {
      title: 'Loan Amortization',
      description: 'Amortization Formula. Extra Payments and Balance. Total Interest. Consolidation and Refinancing. Amortization Tables.',
      icon: <Calculator className="w-8 h-8 text-blue-500" />,
      link: '/loan-amortization',
      buttonText: 'View Calculators',
      color: 'from-blue-400 to-blue-600'
    },
    {
      title: 'Credit Cards',
      description: 'Credit Cards Interest. Fees and Expenses. The Grace Period. Choosing the Best Deal. Average Daily Balances. Tables.',
      icon: <CreditCard className="w-8 h-8 text-green-500" />,
      link: '/credit-cards',
      buttonText: 'View Calculators',
      color: 'from-green-400 to-green-600'
    },
    {
      title: 'Retirement Plans',
      description: 'Defined Benefit Plans. Defined Contribution Plans. Social Security. IRA. 401(k). Annuities. Other Account Types.',
      icon: <PiggyBank className="w-8 h-8 text-purple-500" />,
      link: '/retirement-plans',
      buttonText: 'View Calculators',
      color: 'from-purple-400 to-purple-600',
    },
    {
      title: 'Prices Utilities',
      description: 'Price Comparator. Markup and Markdown. Gross and Net Profit Margin. Price Appreciation. Asset Depreciation. Discounts.',
      icon: <PieChart className="w-8 h-8 text-emerald-500" />,
      link: '/prices',
      buttonText: 'View Utilities',
      color: 'from-emerald-400 to-emerald-600'
    },
    {
      title: 'Cash Flow',
      description: 'Analyze income and expenses. Cash flow projections. Break-even analysis. Working capital management.',
      icon: <DollarSign className="w-8 h-8 text-yellow-500" />,
      link: '/cash-flow',
      buttonText: 'View Calculators',
      color: 'from-yellow-400 to-yellow-600'
    },
    {
      title: 'Installment Plans',
      description: 'Calculate installment payments. Compare different plan options. Determine total cost and interest paid.',
      icon: <Calendar className="w-8 h-8 text-indigo-500" />,
      link: '/installment-plans',
      buttonText: 'View Calculators',
      color: 'from-indigo-400 to-indigo-600'
    },
    {
      title: 'Insurance',
      description: 'Compare insurance premiums. Calculate coverage needs. Analyze risk and potential payouts.',
      icon: <Umbrella className="w-8 h-8 text-pink-500" />,
      link: '/insurance',
      buttonText: 'View Calculators',
      color: 'from-pink-400 to-pink-600'
    },
    {
      title: 'Investments',
      description: 'Portfolio analysis. Return on investment calculations. Risk assessment tools. Asset allocation strategies.',
      icon: <TrendingUp className="w-8 h-8 text-red-500" />,
      link: '/investments',
      buttonText: 'View Calculators',
      color: 'from-red-400 to-red-600'
    },
    {
      title: 'Leasing',
      description: 'Compare lease vs. buy options. Calculate lease payments. Determine residual value and total cost of leasing.',
      icon: <Truck className="w-8 h-8 text-cyan-500" />,
      link: '/leasing',
      buttonText: 'View Calculators',
      color: 'from-cyan-400 to-cyan-600'
    },
    {
      title: 'Mortgages',
      description: 'Calculate mortgage payments. Compare fixed and adjustable rates. Determine affordability and refinancing options.',
      icon: <Home className="w-8 h-8 text-lime-500" />,
      link: '/mortgages',
      buttonText: 'View Calculators',
      color: 'from-lime-400 to-lime-600'
    },
    {
      title: 'Taxes',
      description: 'Estimate income tax liability. Calculate capital gains tax. Analyze tax deductions and credits.',
      icon: <FileText className="w-8 h-8 text-teal-500" />,
      link: '/taxes',
      buttonText: 'View Calculators',
      color: 'from-teal-400 to-teal-600'
    }
  ];

  return (
    <div className="container mx-auto px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col items-center justify-center mb-12"
      >
        <SearchBox />
      </motion.div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {cards.map((card, index) => (
          <MotionCard
            key={card.title}
            className="flex flex-col overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <div className={`h-2 bg-gradient-to-r ${card.color}`} />
            <CardHeader className="flex flex-row items-center space-x-4 pb-2">
              {card.icon}
              <h3 className="text-2xl font-semibold text-gray-800">{card.title}</h3>
            </CardHeader>
            <CardContent className="flex-grow">
              <p className="text-gray-600">{card.description}</p>
            </CardContent>
            <CardFooter className="mt-auto">
              <Button
                asChild
                variant="default"
                className={`w-full bg-gradient-to-r ${card.color} hover:opacity-90`}
              >
                <Link to={card.link} className="inline-flex items-center justify-center">
                  {card.buttonText} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardFooter>
          </MotionCard>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;